import React, { useRef, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { MarginProps, margin } from 'styled-system'
import { get, sortBy } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import {
  formatPhoneNumber,
  formatPhoneNumberIntl,
  getCountryCallingCode,
  isPossiblePhoneNumber,
  Country,
} from 'react-phone-number-input'
import en from 'react-phone-number-input/locale/en.json'

import PopOver from 'components/PopOver'
import { PopOverItem as PopOverItemType } from 'components/PopOver/types'
import Spinner from 'components/Spinner'
import { ErrorMessage } from 'components/TextInput'
import TextInput, { TextInputProps } from 'components/TextInput'

import { PhoneNumber } from 'types/global'
import { WidthProps } from 'types/styled'
import { COUNTRY_CODES } from 'utils/constants'
import { isValidIntlMobileNumber } from 'utils/validation'
import { colors } from 'utils/colors'

export type FormattedPhoneNumber = PhoneNumber & {
  localFormat?: string
  intlFormat?: string
  isValid?: boolean
}

type PhoneInputProps = Omit<TextInputProps, 'value' | 'onChange'> & {
  defaultCountryCode?: string
  onChange: (output: FormattedPhoneNumber) => void
  validateFormat?: boolean
  value?: PhoneNumber
  displayFlex?: boolean
  showInputLoader?: boolean
}

const Option = ({ callingCode, countryName }: { callingCode: string; countryName: string }) => (
  <StyledOption>
    <CountryName>{countryName}</CountryName>
    <>{`+${callingCode}`}</>
  </StyledOption>
)

const countriesOptions: PopOverItemType[] = sortBy(
  COUNTRY_CODES.map((item: Country) => {
    const callingCode = getCountryCallingCode(item)
    const countryName = get(en, item, '')

    return {
      data: callingCode,
      value: item,
      label: countryName,
      node: <Option callingCode={callingCode} countryName={countryName} />,
    }
  }),
  'label',
)

const findCallingCode = (countryISO?: string, countryCode?: number) =>
  countryISO
    ? countriesOptions.find((c) => c.value === countryISO)
    : countriesOptions.find((c) => c.data === `${countryCode}`)

const findCountry = (callingCode: string | undefined) =>
  callingCode
    ? countriesOptions.find((c) => `+${c.data}` === getCountryCallingCode(callingCode as Country))
    : countriesOptions[0]

const PhoneInput = ({
  defaultCountryCode,
  error,
  label,
  name,
  onChange,
  showError = true,
  validateFormat = true,
  value,
  variant,
  errorVariant,
  showInputLoader,
  onBlur,
  customError,
  width = '100%',
  ...props
}: PhoneInputProps) => {
  const marginProps: MarginProps = margin({ ...props, width })
  const defaultCountry = defaultCountryCode ? findCountry(defaultCountryCode) : countriesOptions[0]
  const numberRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()

  const resolveSelectedOption: PopOverItemType | undefined = useMemo(
    () => countriesOptions.find((o) => o.value === value?.countryISO) ?? defaultCountry,
    [value, defaultCountry],
  )

  const handleChange = useCallback(
    (field: string, val: string) => {
      const newValue: PhoneNumber = { ...value, [field]: val }

      newValue.countryCode = Number(
        findCallingCode(field === 'countryISO' ? (val as string) : resolveSelectedOption?.value)?.data,
      )

      const number = newValue?.number ?? ''
      const countryCode = newValue?.countryCode ?? ''
      const fullNumber = `+${countryCode}${number}`
      const localFormat = formatPhoneNumber(fullNumber) || number
      const intlFormat = formatPhoneNumberIntl(fullNumber) || number

      let isValid = false
      if (validateFormat && isPossiblePhoneNumber(fullNumber) && isValidIntlMobileNumber(number)) {
        isValid = true
      }

      onChange({ ...newValue, number, localFormat, intlFormat, isValid })
    },
    [onChange, value, resolveSelectedOption, validateFormat],
  )

  return (
    <PhoneInputWrapper {...marginProps} width={width}>
      {variant === 'labeled' && (
        <LabelText disabled={props.disabled}>
          {t(label)}
          {props.required && <RequiredText>*</RequiredText>}
        </LabelText>
      )}
      <PhoneInputContainer disabled={props.disabled} error={!!error}>
        <StyledPopover>
          <PopOver
            isDisabled={props.disabled}
            inline
            isSearchable
            onChange={(value?: PopOverItemType | PopOverItemType[]) => {
              const tempValue = value as PopOverItemType
              handleChange('countryISO', tempValue.value)
              const numberInput = document.querySelector(
                `.${numberRef.current?.className?.replace(' ', '.')} input[type="text"]`,
              ) as HTMLInputElement
              numberInput && numberInput.focus()
            }}
            options={countriesOptions}
            value={resolveSelectedOption}
          >
            <TextInput
              autoComplete="off"
              disabled={props.disabled}
              icon="angle-down"
              inline
              placeholder=""
              readOnly
              value={resolveSelectedOption ? `+${resolveSelectedOption.data}` : undefined}
              variant="boxless"
              width={100}
              label={`${label} country`}
            />
          </PopOver>
        </StyledPopover>
        <TextInputContainer ref={numberRef}>
          <TextInput
            disabled={props.disabled}
            inline
            maxLength={17}
            name={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('number', e.target.value.toString().match(/[0-9]/g)?.join('') ?? '')
            }
            onBlur={onBlur}
            value={value?.number}
            variant="boxless"
            label={`${label} value`}
            addonAfter={
              showInputLoader && (
                <InputLoaderWrapper>
                  <Spinner fontSize={5} />
                </InputLoaderWrapper>
              )
            }
          />
        </TextInputContainer>
      </PhoneInputContainer>
      {showError &&
        error &&
        (customError ? customError : <ErrorMessage errorVariant={errorVariant}>{error}</ErrorMessage>)}
    </PhoneInputWrapper>
  )
}

const PhoneInputWrapper = styled.div<{ width?: WidthProps }>`
  ${margin}
  width: ${(p) => (typeof p.width === 'number' ? `${p.width}px` : `100%`)};
`

const getColor = (p: { error?: boolean; disabled?: boolean }) => {
  if (p.disabled) return colors.gray5
  if (p.error) return colors.red
  return colors.defaultText
}

const PhoneInputContainer = styled.div<{
  disabled?: boolean
  error?: boolean
}>`
  ${(p) => p.disabled && `background-color: ${p.theme.colors.gray1};`}
  margin-top: -1px;
  border: 1px solid ${getColor};
  border-radius: 4px;
  clear: both;
  display: flex;
  box-sizing: border-box;
  position: relative;

  svg {
    color: ${(p) => (p.disabled ? p.theme.colors.gray5 : p.theme.colors.defaultText)};
  }

  &:active,
  &:focus,
  &:focus-within,
  &:hover {
    border-color: ${(p) => (p.disabled ? p.theme.colors.gray3 : p.theme.colors.blue1)};
  }

  &:active svg,
  &:focus svg,
  &:focus-within svg,
  &:hover svg {
    color: ${(p) => (p.disabled ? p.theme.colors.gray5 : p.theme.colors.blue1)};
  }

  &:active,
  &:focus,
  &:focus-within {
    box-shadow: 0 0 0 4px rgba(143, 155, 179, 0.1);
  }
`

const LabelText = styled.div<{ disabled?: boolean }>`
  ${(p) => p.theme.font({ color: p.disabled ? 'gray4' : 'defaultText' })}
  margin-bottom: 6px;
  text-align: left;
`

const StyledOption = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const CountryName = styled.div`
  flex: 1 0;
  margin: 5px 8px;
`

const RequiredText = styled.span`
  color: ${(p) => p.theme.colors.red};
  margin-left: 4px;
`

const TextInputContainer = styled.div`
  display: inline-block;
  width: 100%;

  input {
    border-radius: 4px;
    box-sizing: border-box;
    width: 100%;
  }
`

const StyledPopover = styled.div`
  display: inline-block;

  input {
    cursor: pointer;
  }

  .pop-over {
    position: initial;

    input {
      border-radius: 4px;
    }
  }

  .pop-over-content {
    width: 100%;
  }
`

const InputLoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  left: -40px;
`

export default PhoneInput
